// Autoría de los extras (bono/descuento) de la liquidación: quién los cargó y cuándo.
//
// Dos fuentes, de más a menos confiable:
//  1. El backend: cada extra llega con `createdBy` / `createdByName` / `createdAt`
//     (billing PR #70). Es el dato de auditoría real y cubre también los extras viejos.
//  2. El sello en la descripción ("· cargado por <nombre> · <fecha>") que agrega este mismo
//     modal al crear un extra. Se mantiene como respaldo mientras el backend con autoría
//     no esté desplegado y para los consumidores que solo ven la descripción.
//
// El motivo original se conserva íntegro al principio; el sello se agrega al final. Los
// extras que carga el propio sistema (auditoría, scripts) siguen la misma convención.

import { formatDateTime } from '../../../../utils/formatters';

const pad = (n) => String(n).padStart(2, '0');

/**
 * Texto de auditoría de un extra a partir de lo que informa el backend:
 * "Cargado por <nombre> · <fecha>", solo "Cargado el <fecha>" si no hay autor resuelto, y
 * '' si el backend no trae nada (extra sin fecha ni autor, o backend anterior).
 * Nunca inventa un autor: sin nombre, no hay nombre.
 *
 * @param {{createdByName?: string|null, createdAt?: string|null}} extra
 * @param {(key: string, opts?: object) => string} t traductor i18n
 */
export function extraAuditLabel(extra, t) {
  const fecha = formatDateTime(extra?.createdAt);
  if (!fecha) return '';
  const nombre = String(extra?.createdByName ?? '').trim();
  return nombre
    ? t('Cargado por {{nombre}} · {{fecha}}', { nombre, fecha })
    : t('Cargado el {{fecha}}', { fecha });
}

/**
 * Nombre para mostrar del operador logueado. Mismo criterio que el resto de la app
 * (`CollapsibleSidebar`): nombre y apellido del usuario, con el token como respaldo.
 * @returns {string} cadena vacía si no hay ningún dato disponible.
 */
export function operatorName(user, userData) {
  const nombre = [userData?.name || user?.name, userData?.lastName || user?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  return nombre || user?.email || userData?.email || '';
}

/**
 * Fecha y hora local del navegador, `DD-MM-YYYY HH:mm`. Es la hora del operador: la que
 * reconoce cuando busca "cuándo se cargó esto".
 */
export function stampNow(now = new Date()) {
  return (
    `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()}` +
    ` ${pad(now.getHours())}:${pad(now.getMinutes())}`
  );
}

/**
 * Descripción final del extra: el motivo tal cual lo escribió el operador + el sello
 * "· cargado por <nombre> · <fecha>". Sin nombre disponible queda solo la fecha: una marca
 * parcial sirve, una inventada no.
 *
 * @param {string} description motivo escrito por el operador (obligatorio en la UI)
 * @param {object} user usuario del token (AuthContext)
 * @param {object} userData ficha del usuario (AuthContext)
 * @param {Date} [now] inyectable para los tests
 */
export function stampExtraDescription(description, user, userData, now = new Date()) {
  const motivo = String(description ?? '').trim();
  const nombre = operatorName(user, userData);
  const sello = [nombre && `cargado por ${nombre}`, stampNow(now)].filter(Boolean).join(' · ');
  return [motivo, sello].filter(Boolean).join(' · ');
}
